import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setChallengeData } from "../../../store/features/CreateChallenge";
import { Container } from "./style";
import { Button, Form, Input, Space, Modal, message, Select, Result } from "antd";
import ReactRouterPrompt from "react-router-prompt";
import MockPhone from "../../../components/MockPhone/index";
import PutUpload from "../../../components/PutUpload/index";
import ChallengePreview from "../../../components/ChallengePreview/index";
import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { LeftOutlined } from "@ant-design/icons";
import useSubscriptionCheck from "../../../hooks/useSubscriptionCheck";
import SubscriptionModal from "../../../components/SubscriptionModal";
import PublishChallengeSteps from "./PublishChallengeSteps";
const { TextArea } = Input;
const DEFAULT_CHALLENGE_BANNER_URL =
  "https://uvita-coach-assets.s3.us-east-2.amazonaws.com/userImages/uvita_default_banner.png";

const GROUPS_BY_COACH = gql`
  query GroupsByCoach($coachId: String!, $isArchived: Boolean) {
    groupsByCoach(coachId: $coachId, isArchived: $isArchived) {
      _id
      name
    }
  }
`;

const CREATE_CHALLENGE = gql`
  mutation createChallenge($createChallengeInput: CreateChallengeInput!) {
    createChallenge(payload: $createChallengeInput) {
      _id
    }
  }
`;

const UPDATE_CHALLENGE = gql`
  mutation updateChallenge($updateChallengeInput: UpdateChallengeInput!) {
    updateChallenge(payload: $updateChallengeInput) {
      _id
    }
  }
`;

const buildVisibilityParams = (visibilityValue) => {
  const param = {
    visibility: "PRIVATE",
    visibilityType: "ALL_SUBSCRIBERS",
  };

  if (visibilityValue?.startsWith("GROUP_")) {
    param.visibilityType = "GROUP";
    param.visibilityGroupId = visibilityValue.replace("GROUP_", "");
  } else if (visibilityValue === "INVITE_ONLY") {
    param.visibilityType = "INVITE_ONLY";
  }

  return param;
};

export default function CreateLink() {
  const location = useLocation();
  const challengeDraft = location.state?.challengeDraft;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isChanged, setIsChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tempBanner, setTempBanner] = useState(DEFAULT_CHALLENGE_BANNER_URL);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [publishDone, setPublishDone] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const [groups, setGroups] = useState([]);
  const userInfo = useSelector((state) => state.userInfo.userInfo);

  const {
    showModal,
    checkAndExecute,
    handleSubscriptionSuccess,
    handleModalCancel,
    upgradePromptKind,
  } = useSubscriptionCheck();

  const isPublishedChallenge = challengeDraft?.status === "PUBLISHED";
  const saveButtonLabel = isPublishedChallenge ? "Save" : "Save as Draft";

  const [form] = Form.useForm();
  const aboutValue = Form.useWatch("about", form);
  const bannerValue = Form.useWatch("banner", form);

  useEffect(() => {
    if (!challengeDraft) {
      message.error("Challenge not found.");
      navigate("/CreateChallenge");
      return;
    }

    let visibilityValue = "ALL_SUBSCRIBERS";

    if (challengeDraft.visibility === "PUBLIC") {
      visibilityValue = "ALL";
    } else if (challengeDraft.visibilityType === "ALL_SUBSCRIBERS") {
      visibilityValue = "ALL_SUBSCRIBERS";
    } else if (
      challengeDraft.visibilityType === "GROUP" &&
      challengeDraft.visibilityGroupId
    ) {
      visibilityValue = `GROUP_${challengeDraft.visibilityGroupId}`;
    } else if (challengeDraft.visibilityType === "INVITE_ONLY") {
      visibilityValue = "INVITE_ONLY";
    }

    form.setFieldsValue({
      banner: challengeDraft.banner || DEFAULT_CHALLENGE_BANNER_URL,
      about: challengeDraft.description || challengeDraft.about || "",
      visibility: visibilityValue,
    });

    setTempBanner(challengeDraft.banner || DEFAULT_CHALLENGE_BANNER_URL);
  }, [challengeDraft, form, navigate]);

  useEffect(() => {
    if (loading) {
      setIsChanged(false);
    }
  }, [loading]);

  const [createChallenge] = useMutation(CREATE_CHALLENGE);
  const [updateChallenge] = useMutation(UPDATE_CHALLENGE);

  const [fetchGroups] = useLazyQuery(GROUPS_BY_COACH, {
    variables: { coachId: userInfo.coach?._id, isArchived: false },
    onCompleted: (data) => {
      if (data?.groupsByCoach) {
        setGroups(data.groupsByCoach);
      }
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (userInfo.coach?._id) {
      fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo.coach?._id]);

  const onUpload = (fileUrl) => {
    setTempBanner(fileUrl);
    form.setFieldsValue({ banner: fileUrl });
  };

  const onFormChange = () => {
    setIsChanged(true);
  };

  const onSubmit = async (values, status = "PUBLISHED", redirectToDashboard = false) => {
    setLoading(true);
    try {
      const visibilityParams = buildVisibilityParams(values.visibility);
      const publishFields = {
        banner: values.banner,
        description: values.about,
        status,
        ...visibilityParams,
      };

      if (!challengeDraft) {
        message.error("Challenge not found.");
        return;
      }

      if (challengeDraft._id) {
        await updateChallenge({
          variables: {
            updateChallengeInput: {
              _id: challengeDraft._id,
              title: challengeDraft.title,
              numberOfDays: challengeDraft.numberOfDays,
              scheduledAt: challengeDraft.scheduledAt,
              days: challengeDraft.days,
              ...publishFields,
            },
          },
        });
      } else {
        await createChallenge({
          variables: {
            createChallengeInput: {
              title: challengeDraft.title,
              numberOfDays: challengeDraft.numberOfDays,
              scheduledAt: challengeDraft.scheduledAt,
              days: challengeDraft.days,
              ...publishFields,
            },
          },
        });
      }

      if (redirectToDashboard) {
        message.success(
          status === "PUBLISHED"
            ? "Challenge has been saved!"
            : "Challenge has been saved as draft!"
        );
        dispatch(setChallengeData({}));
        setIsChanged(false);
        goToContentManager();
        return;
      }

      message.success("Challenge published successfully!");
      setIsChanged(false);
      setPublishDone(true);
    } finally {
      setLoading(false);
    }
  };

  const saveChanges = () => {
    const values = form.getFieldsValue();
    const draftValues = {
      banner:
        values.banner ||
        challengeDraft?.banner ||
        DEFAULT_CHALLENGE_BANNER_URL,
      about: values.about || "",
      visibility: values.visibility || "ALL_SUBSCRIBERS",
    };

    onSubmit(
      draftValues,
      isPublishedChallenge ? "PUBLISHED" : "DRAFTED",
      true
    );
  };

  const onFinish = (values) => {
    checkAndExecute(() => {
      onSubmit(values);
    });
  };

  const onFinishFailed = () => {
    setLoading(false);
  };

  const onPreviewWeb = () => {
    setPreviewVisible(true);
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
  };

  const mapDraftDaysToRowData = (days, hasId) => {
    if (hasId) {
      return (days || []).map((day) => ({
        prompt: { content: day.promptContent || "" },
        insight: {
          content: day.insightContent || "",
          link: day.insightLink || "",
        },
        action: { content: day.actionContent || "" },
      }));
    }
    return (days || []).map((day) => ({
      reflection: day.promptContent || "",
      insight: day.insightContent || "",
      insightLink: day.insightLink || "",
      action: day.actionContent || "",
    }));
  };

  const onBack = () => {
    if (!challengeDraft) {
      navigate("/CreateChallenge");
      return;
    }

    const values = form.getFieldsValue();
    const visibilityParams = buildVisibilityParams(values.visibility);
    const mergedDraft = {
      ...challengeDraft,
      banner: values.banner || challengeDraft.banner,
      description: values.about,
      about: values.about,
      ...visibilityParams,
    };
    const hasId = !!mergedDraft._id;

    dispatch(
      setChallengeData({
        ...(hasId
          ? { _id: mergedDraft._id, status: mergedDraft.status || "DRAFTED" }
          : {}),
        title: mergedDraft.title,
        numberOfDays: mergedDraft.numberOfDays,
        scheduledAt: mergedDraft.scheduledAt,
        days: mapDraftDaysToRowData(mergedDraft.days, hasId),
        banner: mergedDraft.banner,
        description: mergedDraft.description,
        about: mergedDraft.about,
        visibility: mergedDraft.visibility,
        visibilityType: mergedDraft.visibilityType,
        visibilityGroupId: mergedDraft.visibilityGroupId,
      })
    );
    setIsChanged(false);
    navigate(hasId ? "/EditChallenge" : "/CreateChallenge");
  };

  const goToContentManager = useCallback(() => {
    navigate("/ContentManager?tabName=4");
  }, [navigate]);

  useEffect(() => {
    if (!publishDone) return;

    setRedirectCountdown(3);
    let remaining = 3;
    const timer = setInterval(() => {
      remaining -= 1;
      setRedirectCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        goToContentManager();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [publishDone, goToContentManager]);

  const uploadFileName = `${challengeDraft?._id || "new"}_${+new Date()}_banner.png`;

  if (publishDone) {
    return (
      <Container>
        <div className="page-top">
          <PublishChallengeSteps current={2} />
        </div>
        <Button
          className="btn-back"
          icon={<LeftOutlined />}
          type="text"
          onClick={goToContentManager}
        >
          Content Manager
        </Button>
        <Result
          status="success"
          title="Challenge published!"
          subTitle={
            <>
              <div>{`"${challengeDraft?.title}" is now live and visible to your subscribers.`}</div>
              {redirectCountdown > 0 && (
                <div style={{ marginTop: 8, color: "#666" }}>
                  Returning to Content Manager in {redirectCountdown}s...
                </div>
              )}
            </>
          }
          extra={[
            <Button type="primary" key="back" onClick={goToContentManager}>
              Back to Content Manager
            </Button>,
          ]}
        />
      </Container>
    );
  }

  return (
    <Container>
      <ReactRouterPrompt when={isChanged}>
        {({ isActive, onConfirm, onCancel }) =>
          isActive && (
            <Modal
              title="Unsaved changes"
              open
              footer={[
                <div key="flex-between" className="flex-between">
                  <Button
                    key="discard"
                    danger
                    loading={loading}
                    onClick={() => {
                      setIsChanged(false);
                      onConfirm();
                    }}
                  >
                    Discard Changes
                  </Button>
                  <div key="div">
                    <Button key="back" onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button
                      key="save"
                      type="primary"
                      loading={loading}
                      onClick={saveChanges}
                    >
                      {saveButtonLabel}
                    </Button>
                  </div>
                </div>,
              ]}
            >
              <p>
                You are about to leave this page with unsaved changes. Would you
                like to save these changes before leaving?
              </p>
            </Modal>
          )
        }
      </ReactRouterPrompt>
      <div className="page-top">
        <PublishChallengeSteps current={1} />
      </div>
      <div className="link-page">
        <div className="left-side">
          <Button
            className="btn-back"
            icon={<LeftOutlined />}
            type="text"
            onClick={onBack}
          >
            Back
          </Button>
          <div className="page-title">Publish this challenge</div>
          <Form
            form={form}
            name="linkForm"
            layout="vertical"
            size="large"
            className="full-width"
            initialValues={{
              banner: challengeDraft?.banner || DEFAULT_CHALLENGE_BANNER_URL,
              about: challengeDraft?.description || challengeDraft?.about || "",
              visibility: "ALL_SUBSCRIBERS",
            }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            onValuesChange={onFormChange}
          >
            <Form.Item label="Upload a banner picture" name="banner">
              <PutUpload fileName={uploadFileName} onChange={onUpload} />
            </Form.Item>
            <Form.Item
              label="About"
              name="about"
              rules={[{ required: true, message: "Please input about" }]}
            >
              <TextArea
                showCount
                maxLength={1000}
                autoSize={{ minRows: 6, maxRows: 10 }}
                placeholder="Input about"
                style={{ resize: "none" }}
              />
            </Form.Item>

            <Form.Item
              label="Visible to"
              name="visibility"
              rules={[{ required: true, message: "Please select visibility" }]}
            >
              <Select placeholder="Select visibility" size="large">
                <Select.Option value="ALL_SUBSCRIBERS">
                  All Subscribers
                </Select.Option>
                {groups.map((group) => (
                  <Select.Option key={group._id} value={`GROUP_${group._id}`}>
                    {group.name}
                  </Select.Option>
                ))}
                <Select.Option value="INVITE_ONLY">Invite Only</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space size="middle" className="mt-16">
                <Button loading={loading} onClick={saveChanges}>
                  {saveButtonLabel}
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Publish challenge
                </Button>
                <Button onClick={onPreviewWeb}>Preview on web portal</Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
        <div className="right-side">
          <MockPhone showTabbar>
            <ChallengePreview
              challengeData={{
                banner:
                  bannerValue || tempBanner || DEFAULT_CHALLENGE_BANNER_URL,
                title: challengeDraft?.title,
                scheduledAt: challengeDraft?.scheduledAt,
                enrolledCount: challengeDraft?.enrolledCount || 0,
                description: aboutValue,
                coach: userInfo.coach,
              }}
            />
          </MockPhone>
        </div>
      </div>
      <Modal
        title="Web Portal Preview"
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={[
          <Button key="close" onClick={handleClosePreview}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        <ChallengePreview
          challengeData={{
            banner: bannerValue || tempBanner || DEFAULT_CHALLENGE_BANNER_URL,
            title: challengeDraft?.title,
            scheduledAt: challengeDraft?.scheduledAt,
            enrolledCount: challengeDraft?.enrolledCount || 0,
            description: aboutValue,
            coach: userInfo.coach,
          }}
        />
      </Modal>
      <SubscriptionModal
        open={showModal}
        onSuccess={handleSubscriptionSuccess}
        onCancel={handleModalCancel}
        checkoutPromptKind={upgradePromptKind ?? "trial"}
      />
    </Container>
  );
}
